import {createMuiTheme, useTheme} from '@material-ui/core/styles';
import {useMediaQuery} from '@material-ui/core';
import {CremaTheme} from '../../types/AppContextPropsType';
import {Breakpoint} from '@material-ui/core/styles/createBreakpoints';
import moment from 'moment';
import {useIntl} from 'react-intl';
import LettersNotes from 'modules/admin/PatientRecords/PatientRecordsList/LettersNotes';
import { start } from '@popperjs/core';
import { number } from 'yup';
import { Stripe, loadStripe } from '@stripe/stripe-js';

type BreakpointOrNull = Breakpoint | null;

export const isBreakPointDown = (key: 'xs' | 'sm' | 'md' | 'lg' | 'xl') => {
  const defaultTheme = createMuiTheme();
  return defaultTheme.breakpoints.width(key) > window.innerWidth;
};

export const useDownBreakPointChecker = (
  key: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
) => {
  return useMediaQuery((theme: CremaTheme) => theme.breakpoints.down(key));
};

export const useBreakPointDown = (key: 'xs' | 'sm' | 'md' | 'lg' | 'xl') => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down(key));
};

export const useWidth = () => {
  const theme: CremaTheme = useTheme();
  const keys: Breakpoint[] = [...theme.breakpoints.keys].reverse();
  return (
    keys.reduce((output: BreakpointOrNull, key: Breakpoint) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const matches = useMediaQuery(theme.breakpoints.up(key));
      return !output && matches ? key : output;
    }, null) || 'xs'
  );
};

export const createRoutes = (routeConfigs: any[]) => {
  let allRoutes: any[] = [];
  routeConfigs.forEach(config => {
    allRoutes = [...allRoutes, ...setRoutes(config)];
  });
  return allRoutes;
};

export const setRoutes = (config: any) => {
  let routes = [...config.routes];
  if (config.auth) {
    routes = routes.map(route => {
      let auth = route.auth
        ? [...config.auth, ...route.auth]
        : [...config.auth];
      return {...route, auth};
    });
  }

  return [...routes];
};
export const getBreakPointsValue = (valueSet: any, breakpoint: string) => {
  if (typeof valueSet === 'number') return valueSet;
  switch (breakpoint) {
    case 'xs':
      return valueSet.xs;
    case 'sm':
      return valueSet.sm || valueSet.xs;
    case 'md':
      return valueSet.md || valueSet.sm || valueSet.xs;
    case 'lg':
      return valueSet.lg || valueSet.md || valueSet.sm || valueSet.xs;
    default:
      return (
        valueSet.xl || valueSet.lg || valueSet.md || valueSet.sm || valueSet.xs
      );
  }
};
export const getFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  let k = 1024,
    dm = 2,
    sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const getCustomDateTime = (
  value = 0,
  unit = 'days',
  format = 'YYYY-MM-DD',
): string => {
  if (value === 0) {
    return moment().format(format) as string;
  } else {
    // @ts-ignore
    return moment().add(value, unit)
      .format(format) as string;
  }
};

export const timeFromNow = (date: string) => {
  const timestamp = +moment(date).format('X');
  const newDate = moment.unix(timestamp);
  return moment(newDate).fromNow();
};

// 'intl' service singleton reference
let intl: any;

export function IntlGlobalProvider({children}: any) {
  intl = useIntl();
  // Keep the 'intl' service reference
  return children;
}

export const appIntl = () => {
  return intl;
};
export const checkPermission = (
  routeAuth: any | null | undefined,
  userRole: any | null | undefined,
) => {
  if (routeAuth === null || routeAuth === undefined) {
    return true;
  }

  if (userRole && Array.isArray(userRole)) {
    return routeAuth.some((r: any) => userRole.indexOf(r) >= 0);
  }

  if (routeAuth.length === 0) {
    return !userRole || userRole.length === 0;
  }
  if (userRole && Array.isArray(userRole) && Array.isArray(routeAuth)) {
    return routeAuth.some(r => userRole.indexOf(r) >= 0);
  }
  return routeAuth.indexOf(userRole) >= 0;
};

export const getCalendarWeekStartAndEndDate = (numberOfWeek: number) => {
  let weekDate: any = moment().startOf('week');
  let weekStartDate: any;
  let weekEndDate = new Date().valueOf()
  for (let week=1 ; week <= numberOfWeek ; week++ ) {
    if (week == numberOfWeek) {
      weekStartDate = moment(weekDate, "YYYY-MM-DD").add({days: 0}).format("YYYY-MM-DD")
    }
    weekDate = weekDate.subtract({ days: 7 });
  }
  return { start: new Date(weekStartDate).valueOf(), end: weekEndDate}
}

export const getCalendarMonthsStartAndEndDate = (numberOfMonths: number) => {
  let currentMonthStartDate: any = moment().startOf('month');
  let monthEndDate: any = moment().endOf('month').format("YYYY-MM-DD")
  const monthStartDate: any = currentMonthStartDate.subtract(numberOfMonths-1, 'months').format('YYYY-MM-DD')
  return {start:new Date(monthStartDate).valueOf(), end: new Date(monthEndDate).valueOf()}
}
export const getWeekDays = (numberOfWeek:number, startDate?: any) => {
  let weekStart: any = moment().startOf('week')
  if (startDate) {
    weekStart = moment(startDate).startOf('week');
  }
  let weeks: any = []
  for (let week=1 ; week <= numberOfWeek ; week++ ) {
    let weekDays: any = {};
    weekDays = {
      name: `week${week}`,
      startDate:  moment(weekStart, "YYYY-MM-DD").add({days: 0}).format("YYYY-MM-DD"),
      endDate: moment(weekStart, "YYYY-MM-DD").add({days: 6}).format("YYYY-MM-DD")
    }
    weeks.push(weekDays);
    weekStart = weekStart.subtract({ days: 7 });
  }
  return weeks;
}

export const getCalendarMonths = (numberOfMonths: number) => {
  let monthStart: any = moment().startOf('month');
  let monthsArray: any = []
  for (let month=1 ; month <= numberOfMonths ; month++ ) {
    let months: any = {}
    months = {
      name: monthStart.format('MMMM'),
      numericMonth: monthStart.format('M'),
      startDate: monthStart.format("YYYY-MM-DD"),
      endDate: moment(monthStart).endOf('month').format("YYYY-MM-DD")
    }
    monthStart = monthStart.subtract(1, "month")
    monthsArray.push(months)
  }
  return monthsArray.reverse();
}

export const getLastNdaysStartAndEndTime = (noOfDays: number, startDate?: any) => {
  let datesArray: any = []
  for (let days=0 ; days < noOfDays ; days++ ) {
    let daysObj: any = {}
    var d = new Date();
    if (startDate) { 
      d = new Date(startDate)
    }  
    var yesterday = d.setDate(d.getDate() - days);
    const dateString = moment(yesterday).format('DD-MM-YYYY')
    const endDate = new Date(yesterday).setHours(23,59,59, 990)
    daysObj = {
      name: dateString,
      startDate: new Date(yesterday).setHours(0,0,0,0),
      endDate: endDate
    }
    datesArray.push(daysObj)
  }
  return datesArray.reverse();
}

export const getCalendarMonthsFromDateRange = (startDate: any, endDate: any, custom?:boolean) => {
  let date1 = new Date(startDate)
  let date2 = new Date(endDate)
  if (!custom) {
    let appdatearray = startDate?.split("-");
    date1 = new Date(appdatearray[2] + "-" + appdatearray[1] + "-" + appdatearray[0]);
    let appdatearrayB = endDate?.split("-");
    date2 = new Date(appdatearrayB[2] + "-" + appdatearrayB[1] + "-" + appdatearrayB[0]);
  }
  var year1=date1.getFullYear();
  var year2=date2.getFullYear();
  var month1=date1.getMonth();
  var month2=date2.getMonth();
  let numberOfMonths = (year2 - year1) * 12 + (month2 - month1) + 1;
  let monthStart: any = moment(date1).startOf('month');
  let monthsArray: any = []
  for (let month=1 ; month <= numberOfMonths ; month++ ) {
    let months: any = {}
    months = {
      name: monthStart.format('MMMM'),
      numericMonth: monthStart.format('M'),
      startDate: monthStart.format("YYYY-MM-DD"),
      endDate: moment(monthStart).endOf('month').format("YYYY-MM-DD")
    }
    monthStart = monthStart.add(1, "month")
    monthsArray.push(months)
  }
  return monthsArray;
}

export const getAppoinmentTrendMetricsByTimeline = (timelineDetail, appointmentList) => {
  const currentDate = new Date().valueOf()
    let chartData: any = []
    timelineDetail?.forEach((month) => {
      const lableName = month.name
      // const currentMonth = new Date().getMonth() + 1
      // if (month.numericMonth > currentMonth) return
      const appointmentsByType: any = appointmentList?.reduce((appointments: any, item) => {
           let appdatearray = item.appointmentDate?.split("-");
          const newAppDate = new Date(
              appdatearray[2] + "-" + appdatearray[1] + "-" + appdatearray[0]
            ).valueOf();
          if (newAppDate >= new Date(month?.startDate).valueOf() && newAppDate <= new Date(month?.endDate).valueOf()) {
              if (item.appointmentStatus === "Completed") {
                appointments.completed += 1
              }
              if (item.appointmentStatus === "Cancelled") {
                appointments.cancelled += 1
              }
              if (item.appointmentStatus === "Approved" && item.appointmentMode === "Video Consultation" && !item.rescheduledOn && newAppDate < currentDate) {
                appointments.didNotAttend += 1
              }
            }
            return appointments
      }, {name: lableName, completed: 0,cancelled: 0, didNotAttend: 0})
      chartData.push(appointmentsByType)
    })
    let appointmentsDetails: any = []
    const cancelledData: any = []
    const completedData: any = []
    const didNotAttendData: any = []
    const chartNames: any = []
    chartData?.forEach((data) => {
        cancelledData.push(data.cancelled)
        completedData.push(data.completed)
        didNotAttendData.push(data.didNotAttend)
        chartNames.push(data.name)
    })
    appointmentsDetails.push( {label: "Completed", data: completedData,  backgroundColor: 'rgb(24,45,88)'}, {label: "Cancelled", data: cancelledData,  backgroundColor: 'rgb(252,134,33)'}, {label: "DidNotAttend", data: didNotAttendData, backgroundColor: 'rgb(29,98,163)'})
    return {chartNames, appointmentsDetails}
}

export const getReviewsTrendMetricsByTimeline = (monthsDetail, reviewsList) => {
  const currentDate = new Date().valueOf()
    let chartData: any = []
    monthsDetail?.forEach((month) => {
      const monthName = month.name
      // const currentMonth = new Date().getMonth() + 1
      // if (month.numericMonth > currentMonth) return
      const reviewsByType: any = reviewsList?.reduce((reviews: any, item) => {
          const newAppDate = new Date(
              item.createdOn
            ).valueOf();
          if (newAppDate >= new Date(month?.startDate).valueOf() && newAppDate <= new Date(month?.endDate).valueOf()) {
              if (item.rating == 1) {
                reviews.oneStar += 1
              }
              if (item.rating == 2) {
                reviews.twoStar += 1
              }
              if (item.rating == 3) {
                reviews.threeStar += 1
              }
              if (item.rating == 4) {
                reviews.fourStar += 1
              }
              if (item.rating == 5) {
                reviews.fiveStar += 1
              }
            }
            return reviews
      }, {name: monthName, oneStar: 0, twoStar: 0, threeStar: 0, fourStar: 0, fiveStar:0})
      chartData.push(reviewsByType)
    })
    let reviewsListDetails: any = []
    const oneStar: any = []
    const twoStar: any = []
    const threeStar: any = []
    const fourStar: any = []
    const fiveStar: any = []
    const chartNames: any = []
    chartData?.forEach((data) => {
        oneStar.push(data.oneStar)
        twoStar.push(data.twoStar)
        threeStar.push(data.threeStar)
        fourStar.push(data.fourStar)
        fiveStar.push(data.fiveStar)
        chartNames.push(data.name)
    })
    reviewsListDetails.push({label: "1 star", data: oneStar,  backgroundColor: 'rgba(24, 45, 88)'}, 
    {label: "2 star", data: twoStar,  backgroundColor: 'rgb(71,183,228)'}, 
    {label: "3 star", data: threeStar, backgroundColor: 'rgb(36,173,145)'},
    {label: "4 star", data: fourStar, backgroundColor: 'rgb(252,134,33)'},
    {label: "5 star", data: fiveStar, backgroundColor: 'rgb(29,98,163)'},
    )
  return {chartNames, reviewsListDetails}
}

export function getFileExtension(url: string): string {
  if (!url) return '';

  const pathPart = url.split(/[?#]/)[0];
  const filename = pathPart.split('/').pop() || '';

  const match = filename.match(/\.([a-z0-9]+)(?=_|$)/i);

  return match ? match[1].toLowerCase() : '';
}


export const getCurrentAge=(date:Date)=>{
  return moment().diff(date, 'years', false)
}

export const getCurrentAgeWithYearFormat=(data)=>{

  

  let dateofbirth =yearFormat(data)
  let from = dateofbirth.split("-");
  let birthdateTimeStamp:any = new Date(from[2], from[1] - 1, from[0]);
 

return   getCurrentAge(birthdateTimeStamp);
}

export const yearFormat=(date) =>{
  if (date === undefined || date === null || date.toString().trim() === '') {
    return ''; // or any default value you prefer
  }
  
  let str = date;
  // If it's the new UTC ISO format
  if (str.includes('T') && str.includes('Z')) {
    return moment(str).local().format('DD-MM-YYYY');
  }

  // Returns 12
  if (str.length < 10) {
    return date;
  } else {
    var x = new Date(date);
    var y = x.getFullYear().toString();
    var m = (x.getMonth() + 1).toString();
    var d = x.getDate().toString();
    d.length == 1 && (d = "0" + d);
    m.length == 1 && (m = "0" + m);

    var ddmmyyyy = d + "-" + m + "-" + y;

    return ddmmyyyy;
  }
}

export const formatDateLocal = (dateStr: string, formatStr: string = 'DD-MM-YYYY') => {
  if (!dateStr || dateStr === 'null' || dateStr === 'undefined') return '';
  
  // New UTC ISO format (contains T and ends with Z)
  if (dateStr.toString().includes('T') && dateStr.toString().includes('Z')) {
    return moment(dateStr).local().format(formatStr);
  }
  
  // Legacy DD-MM-YYYY format
  if (dateStr.toString().match(/^\d{2}-\d{2}-\d{4}$/)) {
    return moment(dateStr, 'DD-MM-YYYY').format(formatStr);
  }

  // Fallback for other formats
  return moment(dateStr).format(formatStr);
};

export const formatTimeLocal = (dateStr: string, formatStr: string = 'hh:mm A') => {
  if (!dateStr || dateStr === 'null' || dateStr === 'undefined') return '';
  
  // If it's the new UTC format, parse and localize
  if (dateStr.toString().includes('T') && dateStr.toString().includes('Z')) {
    return moment(dateStr).local().format(formatStr);
  }
  
  // For legacy 'hh:mm A' strings, they are usually already in the correct display string
  if (dateStr.toString().toLowerCase().includes('am') || dateStr.toString().toLowerCase().includes('pm')) {
    return dateStr;
  }
  
  // General fallback
  return moment(dateStr).format(formatStr);
}

const hasTimezoneInfo = (value: string) => /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);

export const formatDateTimeLocal = (
  dateStr: string,
  formatStr: string = "DD-MM-YYYY hh:mm A"
) => {
  if (!dateStr || dateStr === "null" || dateStr === "undefined") return "";

  const value = dateStr.toString().trim();
  const parsed = hasTimezoneInfo(value) ? moment(value) : moment.utc(value);

  if (!parsed.isValid()) {
    return value;
  }

  return parsed.local().format(formatStr);
}


export let stripePromise: Promise<Stripe | null>;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);
  }
  return stripePromise;
};

getStripe();
